/**
 * Which tabs the personalization sheet shows, React-free by mandate (epic #26).
 *
 * ui#648 turned the sheet from three stacked editors in one scrolling window into a tabbed one. That
 * is a PRESENTATION change: the sections, their gates and their save/reset flows are untouched, and
 * this module exists so the one rule the reflow introduced — which tabs exist for a given plan —
 * cannot drift from the rule it replaced.
 *
 * The rule it replaced was a single `&&` in `BrandSettings`: the download-page theme rendered only for
 * a sender with `customerLogoAndColors`, while the brand and address editors always rendered (locked,
 * for a sender without the capability). Reproduced exactly here — a theme TAB that opened onto nothing
 * would be a new, worse behaviour than the section that simply was not there.
 *
 * {@link ./brandSheetUpsell} still decides WHOSE upsell the sheet shows; this only says which tab that
 * section now lives in. Deliberately delegating rather than re-deriving: two functions answering
 * "who is locked" from the same flags is how they end up disagreeing.
 */

import {brandSheetUpsell, type BrandSheetEntitlements, type BrandSheetUpsell} from "./brandSheetUpsell";

/** The sheet's three areas. Also the i18n key of each tab's label. */
export type PersonalizationTabId = "brand" | "theme" | "domain";

/** Every tab in display order, whether or not the current plan sees it. */
export const PERSONALIZATION_TAB_ORDER: readonly PersonalizationTabId[] = ["brand", "theme", "domain"];

/**
 * The tabs a sender actually sees, in display order.
 *
 * `brand` and `domain` are always present — both render a locked state of their own for a sender
 * without the capability, exactly as they did when they were stacked sections. `theme` is present only
 * with `customerLogoAndColors`, because that section was absent entirely without it.
 */
export function personalizationTabs(entitlements: BrandSheetEntitlements): readonly PersonalizationTabId[] {
	return PERSONALIZATION_TAB_ORDER.filter(tab => tab !== "theme" || entitlements.brandEntitled);
}

/**
 * The tab the sheet opens on: the first one, which is the section that used to be at the top of the
 * scroll. Never the locked one — landing a sender on an upsell they did not ask for would make the
 * sheet feel like a paywall rather than an editor.
 */
export function defaultPersonalizationTab(entitlements: BrandSheetEntitlements): PersonalizationTabId {
	return personalizationTabs(entitlements)[0];
}

/**
 * The tab carrying the sheet's single upsell, or `null` when nothing is locked.
 *
 * The note still renders exactly once, inside whichever panel owns it — which is what keeps the ≤ 1
 * invariant `brandSheetUpsell` was written for, and with it the Playwright strict-mode guarantee that
 * one accessible string resolves one element.
 */
export function personalizationUpsellTab(entitlements: BrandSheetEntitlements): PersonalizationTabId | null {
	const upsell: BrandSheetUpsell = brandSheetUpsell(entitlements);
	if (upsell === "none") {
		return null;
	}
	return upsell === "brand" ? "brand" : "domain";
}
